import { createApi } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import {
  AulaConteudoData,
  AulaEadListItem,
  CapituloEad,
  CreateAulaTrilhaDto,
  CreateCapituloEadDto,
  PatchCapituloEadDto,
  ReorderCapitulosEadDto,
  ReorderAulasTrilhaEadDto,
  PatchAulaEadDto,
  PlayerEadData,
  PreviewVideoEadData,
  ProgressoEadPlayer,
  RelatorioEadTurma,
  VisualizacoesAulaEad,
  SalvarProgressoEadDto,
  TrilhaProfessorData,
} from "@/interfaces/ensino-ead.interface";
import { RESOURCES } from "./resources";

const ensinoEadBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const { baseQueryWithReauth } = await import("@/lib/api");
  return baseQueryWithReauth(args, api, extraOptions);
};

export const ensinoEadAPI = createApi({
  reducerPath: "ensinoEadAPI",
  baseQuery: ensinoEadBaseQuery,
  tagTypes: [RESOURCES.ENSINO_EAD],
  endpoints: (build) => ({
    getConteudoAula: build.query<
      AulaConteudoData,
      { reuniaoId: number; loginId?: number; participanteId?: number }
    >({
      query: ({ reuniaoId, loginId, participanteId }) => ({
        url: `/ensino-ead/aulas/${reuniaoId}/conteudo`,
        params: {
          ...(loginId ? { loginId } : {}),
          ...(participanteId ? { participanteId } : {}),
        },
      }),
      providesTags: (_r, _e, { reuniaoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `conteudo-${reuniaoId}` },
      ],
    }),

    getPlayerEad: build.query<
      PlayerEadData,
      { reuniaoId: number; loginId?: number; participanteId?: number }
    >({
      query: ({ reuniaoId, loginId, participanteId }) => ({
        url: `/ensino-ead/aulas/${reuniaoId}/player`,
        params: {
          ...(loginId ? { loginId } : {}),
          ...(participanteId ? { participanteId } : {}),
        },
      }),
      providesTags: (_r, _e, { reuniaoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `player-${reuniaoId}` },
      ],
    }),

    getPreviewVideoEad: build.query<
      PreviewVideoEadData,
      { reuniaoId: number; loginId?: number }
    >({
      query: ({ reuniaoId, loginId }) => ({
        url: `/ensino-ead/aulas/${reuniaoId}/preview`,
        params: loginId ? { loginId } : undefined,
      }),
      providesTags: (_r, _e, { reuniaoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `preview-${reuniaoId}` },
      ],
    }),

    getVisualizacoesAulaEad: build.query<
      VisualizacoesAulaEad,
      { reuniaoId: number; loginId?: number }
    >({
      query: ({ reuniaoId, loginId }) => ({
        url: `/ensino-ead/aulas/${reuniaoId}/visualizacoes`,
        params: loginId ? { loginId } : undefined,
      }),
      providesTags: (_r, _e, { reuniaoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `visualizacoes-${reuniaoId}` },
      ],
    }),

    listarAulasEadTurma: build.query<
      AulaEadListItem[],
      { eventoId: number; loginId?: number; participanteId?: number }
    >({
      query: ({ eventoId, loginId, participanteId }) => ({
        url: `/ensino-ead/turmas/${eventoId}/aulas`,
        params: {
          ...(loginId ? { loginId } : {}),
          ...(participanteId ? { participanteId } : {}),
        },
      }),
      providesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `aulas-${eventoId}` },
      ],
    }),

    listarTrilhaProfessor: build.query<
      TrilhaProfessorData,
      { eventoId: number; loginId?: number }
    >({
      query: ({ eventoId, loginId }) => ({
        url: `/ensino-ead/turmas/${eventoId}/trilha`,
        params: loginId ? { loginId } : undefined,
      }),
      providesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `trilha-${eventoId}` },
      ],
    }),

    relatorioEadTurma: build.query<RelatorioEadTurma, { eventoId: number; loginId?: number }>({
      query: ({ eventoId, loginId }) => ({
        url: `/ensino-ead/turmas/${eventoId}/relatorio`,
        params: loginId ? { loginId } : undefined,
      }),
      providesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `relatorio-${eventoId}` },
      ],
    }),

    patchAulaEad: build.mutation<
      unknown,
      { reuniaoId: number; loginId?: number; body: PatchAulaEadDto }
    >({
      query: ({ reuniaoId, loginId, body }) => ({
        url: `/ensino-ead/aulas/${reuniaoId}`,
        method: "PATCH",
        params: loginId ? { loginId } : undefined,
        body,
      }),
      invalidatesTags: (_r, _e, { reuniaoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `player-${reuniaoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `preview-${reuniaoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `conteudo-${reuniaoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `visualizacoes-${reuniaoId}` },
        RESOURCES.ENSINO_EAD,
      ],
    }),

    uploadVideoAulaEad: build.mutation<
      { Id: number },
      { reuniaoId: number; loginId?: number; file: File; duracaoVideoSegundos?: number }
    >({
      query: ({ reuniaoId, loginId, file, duracaoVideoSegundos }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (loginId) formData.append("LoginId", String(loginId));
        if (duracaoVideoSegundos != null && duracaoVideoSegundos > 0) {
          formData.append("DuracaoVideoSegundos", String(Math.floor(duracaoVideoSegundos)));
        }
        return {
          url: `/ensino-ead/aulas/${reuniaoId}/video`,
          method: "POST",
          params: loginId ? { loginId } : undefined,
          body: formData,
        };
      },
      invalidatesTags: (_r, _e, { reuniaoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `player-${reuniaoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `preview-${reuniaoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `visualizacoes-${reuniaoId}` },
        RESOURCES.ENSINO_EAD,
      ],
    }),

    salvarProgressoEad: build.mutation<ProgressoEadPlayer, SalvarProgressoEadDto>({
      query: (body) => ({
        url: `/ensino-ead/progresso`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { ReuniaoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `visualizacoes-${ReuniaoId}` },
      ],
    }),

    createAulaTrilha: build.mutation<
      { Id: number },
      { eventoId: number; loginId?: number; body: CreateAulaTrilhaDto }
    >({
      query: ({ eventoId, loginId, body }) => ({
        url: `/ensino-ead/turmas/${eventoId}/aulas`,
        method: "POST",
        params: loginId ? { loginId } : undefined,
        body,
      }),
      invalidatesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `trilha-${eventoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `aulas-${eventoId}` },
        RESOURCES.ENSINO_EAD,
      ],
    }),

    listarCapitulosEad: build.query<CapituloEad[], { eventoId: number; loginId?: number }>({
      query: ({ eventoId, loginId }) => ({
        url: `/ensino-ead/turmas/${eventoId}/capitulos`,
        params: loginId ? { loginId } : undefined,
      }),
      providesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `capitulos-${eventoId}` },
      ],
    }),

    createCapituloEad: build.mutation<
      CapituloEad,
      { eventoId: number; loginId?: number; body: CreateCapituloEadDto }
    >({
      query: ({ eventoId, loginId, body }) => ({
        url: `/ensino-ead/turmas/${eventoId}/capitulos`,
        method: "POST",
        params: loginId ? { loginId } : undefined,
        body,
      }),
      invalidatesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `capitulos-${eventoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `trilha-${eventoId}` },
        RESOURCES.ENSINO_EAD,
      ],
    }),

    deleteCapituloEad: build.mutation<
      unknown,
      { capituloId: number; loginId?: number; eventoId: number }
    >({
      query: ({ capituloId, loginId }) => ({
        url: `/ensino-ead/capitulos/${capituloId}`,
        method: "DELETE",
        params: loginId ? { loginId } : undefined,
      }),
      invalidatesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `capitulos-${eventoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `trilha-${eventoId}` },
        RESOURCES.ENSINO_EAD,
      ],
    }),

    patchCapituloEad: build.mutation<
      unknown,
      { capituloId: number; loginId?: number; eventoId: number; body: PatchCapituloEadDto }
    >({
      query: ({ capituloId, loginId, body }) => ({
        url: `/ensino-ead/capitulos/${capituloId}`,
        method: "PATCH",
        params: loginId ? { loginId } : undefined,
        body,
      }),
      invalidatesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `capitulos-${eventoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `trilha-${eventoId}` },
        RESOURCES.ENSINO_EAD,
      ],
    }),

    reorderCapitulosEad: build.mutation<
      { ok: boolean },
      { eventoId: number; loginId?: number; body: ReorderCapitulosEadDto }
    >({
      query: ({ eventoId, loginId, body }) => ({
        url: `/ensino-ead/turmas/${eventoId}/capitulos/reorder`,
        method: "PATCH",
        params: loginId ? { loginId } : undefined,
        body,
      }),
      invalidatesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `capitulos-${eventoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `trilha-${eventoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `aulas-${eventoId}` },
        RESOURCES.ENSINO_EAD,
      ],
    }),

    reorderAulasTrilhaEad: build.mutation<
      { ok: boolean },
      { eventoId: number; loginId?: number; body: ReorderAulasTrilhaEadDto }
    >({
      query: ({ eventoId, loginId, body }) => ({
        url: `/ensino-ead/turmas/${eventoId}/aulas/reorder`,
        method: "PATCH",
        params: loginId ? { loginId } : undefined,
        body,
      }),
      invalidatesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_EAD, id: `trilha-${eventoId}` },
        { type: RESOURCES.ENSINO_EAD, id: `aulas-${eventoId}` },
        RESOURCES.ENSINO_EAD,
      ],
    }),
  }),
});

export const {
  useGetConteudoAulaQuery,
  useGetPlayerEadQuery,
  useGetPreviewVideoEadQuery,
  useGetVisualizacoesAulaEadQuery,
  useLazyGetPlayerEadQuery,
  useListarAulasEadTurmaQuery,
  useListarTrilhaProfessorQuery,
  useRelatorioEadTurmaQuery,
  usePatchAulaEadMutation,
  useUploadVideoAulaEadMutation,
  useSalvarProgressoEadMutation,
  useCreateAulaTrilhaMutation,
  useListarCapitulosEadQuery,
  useCreateCapituloEadMutation,
  useDeleteCapituloEadMutation,
  usePatchCapituloEadMutation,
  useReorderCapitulosEadMutation,
  useReorderAulasTrilhaEadMutation,
} = ensinoEadAPI;
